import { useId } from "react";
import { useSelector } from "react-redux";

import UserChatBubble2 from "@/Components/Forms/FirmUserBtn2";

import { getCoCounselInviteStatus } from "../CoCounsel/coCounsel.mjs";

import "./CaseWorkers.css";

// Read-only view of another firm's six case positions. No attach control:
// each firm only edits its own slots.
const FirmTeamPanel = ({ title, firmUsers, workers, positions, inviteStatus }) => {
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);
  const inviteNoteId = useId();
  const slots = Array.from({ length: 6 }, (_, index) => {
    const slot = index + 1;
    const position = positions?.find((entry) => entry.slot === slot);
    return {
      slot,
      // Keep explicit null roles and staff in their slots rather than shifting
      // positions or borrowing roles from the viewing firm's top-level workers.
      role: workers && index < workers.length ? workers[index] : (position?.role ?? null),
      staff: position ? position.staff : (firmUsers?.[index] ?? null),
    };
  });

  const renderTable = (rows, tableClass) => {
    return (
      <div
        className="table-responsive d-flex-1 table--no-card rounded-0 border-0"
        style={{ height: "75px", overflow: "hidden" }}
      >
        <table
          className={`table table-borderless table-striped table-workers co-counsel-team-table ${tableClass} has-height-25 has-table-sub-panel`}
          aria-label={title}
        >
          <tbody>
            {rows.map(({ slot, role, staff }) => {
              const user = staff?.user ?? staff;
              const isStaffActive = staff?.is_active ?? user?.is_active;
              const isWaitingForInvite = isStaffActive === false && inviteStatus === "not_sent";
              return (
                <tr key={slot}>
                  <td className="co-counsel-worker-phone" aria-hidden="true" />
                  <th scope="row" className="co-counsel-worker-role text-start text-black">
                    <span className="d-block text-truncate" title={role?.name}>
                      {role?.name || ""}
                    </span>
                  </th>
                  <td className="text-black">
                    {staff && (
                      <div
                        className={"d-flex align-items-center justify-content-center w-100"}
                        title={
                          isWaitingForInvite
                            ? "Will get an email when you send the invite."
                            : undefined
                        }
                        role={isWaitingForInvite ? "group" : undefined}
                        aria-label={
                          isWaitingForInvite
                            ? `${user?.first_name || ""} ${user?.last_name || ""} — Awaiting invite`
                            : undefined
                        }
                        aria-describedby={isWaitingForInvite ? inviteNoteId : undefined}
                      >
                        <UserChatBubble2
                          userId={user?.id}
                          first_name={user?.first_name ?? ""}
                          last_name={user?.last_name ?? ""}
                          profilePic={staff.profile_pic_29p ?? user?.profile_pic_29p ?? ""}
                          isActive={userOnlineStatus?.[user?.id]?.isOnline ?? false}
                          is_active={user?.is_active}
                          hideIcon
                          width={19}
                        />
                      </div>
                    )}
                  </td>
                  <td className="co-counsel-worker-access" aria-hidden="true" />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="m-b-5 p-l-0">
      <div className="position-relative overflow-hidden">
        <div className="expandable-section">
          <div className="p-l-5 height-25 menu-bar-style">
            <h4 className="client-contact-title text-center d-flex justify-content-center align-items-center h-100">
              {title}
            </h4>
          </div>

          <div className="d-md-flex d-xl-flex flex-md-column w-100 flex-xl-row f-gap-05">
            {renderTable(slots.slice(0, 3), "table-workers-1st")}
            {renderTable(slots.slice(3), "table-workers-2nd")}
          </div>
        </div>
      </div>
    </div>
  );
};

export const OtherFirmTeams = () => {
  const caseSummary = useSelector((state) => state.caseData?.summary);
  const teamScope = caseSummary?.team_scope;
  if (!teamScope) return null;

  const panels = [];

  // co-counsel viewer: the owner firm's team comes first
  const ownerFirm = caseSummary?.case_owner_firm;
  if (teamScope.role === "cocounsel" && ownerFirm) {
    panels.push({
      key: `owner-${ownerFirm.firm?.id}`,
      officeName: ownerFirm.firm?.attorneyprofile?.office_name,
      firmUsers: ownerFirm.firm_users,
      workers: ownerFirm.workers,
      positions: ownerFirm.positions,
    });
  }

  (caseSummary?.case_councelling_firms ?? [])
    // the viewer's own co-counsel entry is already shown in the main slots
    .filter((entry) => entry?.firm?.id !== teamScope.cocounsel_id)
    .forEach((entry) => {
      panels.push({
        key: `cocounsel-${entry.firm?.id}`,
        officeName: entry.firm?.counseling_firm?.attorneyprofile?.office_name,
        firmUsers: entry.firm_users,
        workers: entry.workers,
        positions: entry.positions,
        inviteStatus: getCoCounselInviteStatus(entry),
      });
    });
  return panels.map((panel) => (
    <FirmTeamPanel
      key={panel.key}
      title={`${(panel.officeName || "Co-Counsel Firm").toUpperCase()} — CASE WORKERS`}
      firmUsers={panel.firmUsers}
      workers={panel.workers}
      positions={panel.positions}
      inviteStatus={panel.inviteStatus}
    />
  ));
};
